import Vue from 'vue'
import Vuex from 'vuex'
import { backend, remax, setAuthInterceptor } from '@/api';

Vue.use(Vuex)

export default new Vuex.Store({
  strict: true,

  state: {
    loading: false,

    provinces: [],

    towns: [],

    propertyTypes: [],

    propertyMacroTypes: [],

    priceRange: [],

    filters: {
      buying: true,
      priceRange: {},
      propertyMacroTypes: [],
      towns: []
    },

    user: {
      authenticated: false,
      name: '',
      email: '',
      likedProperties: [],
      dislikedProperties: []
    },

    likedPropertiesData: [],

    properties: [],

    searchMeta: {
      NumberOfReturnedListings: 0,
      TotalSearchResults: 0
    }
  },

  mutations: {
    updateProvinces(state, payload) {
      state.provinces = payload;
    },

    updateTowns(state, payload) {
      state.towns = payload;
    },

    updatePropertyTypes(state, { PropertyTypes, PropertyMacroTypes }) {
      const selectedPropertyMacroTypes = [1, 2, 3, 4, 5, 6, 7, 8];
      const filteredPropertyTypes = selectedPropertyMacroTypes.map(id => PropertyMacroTypes.find(type => type.Id === id));

      state.propertyMacroTypes = filteredPropertyTypes;
      state.propertyTypes = PropertyTypes;
    },

    updatePriceRange(state, payload) {
      state.priceRange = payload;
    },

    updateTownsFilter(state, payload) {
      state.filters.towns = payload;
    },

    updateBuyingFilter(state, payload) {
      state.filters.buying = payload;
    },

    updatePropertyMacroTypesFilter(state, payload) {
      state.filters.propertyMacroTypes = payload;
    },

    updatePriceRangeFilter(state, payload) {
      state.filters.priceRange = payload;
    },

    updateLikedProperties(state, payload) {
      state.user.likedProperties = payload;
    },

    updateLikedPropertiesData(state, payload) {
      state.likedPropertiesData = payload;
    },

    updateDislikedProperties(state, payload) {
      state.user.dislikedProperties = payload;
    },

    updateProperties(state, payload) {
      state.properties = payload;
    },

    updateSearchMeta(state, payload) {
      state.searchMeta = payload;
    },

    updateUser(state, payload) {
      state.user.authenticated = payload.authenticated || false;
      state.user.name = payload.user.name || '';
      state.user.email = payload.user.email || '';
      state.user.likedProperties = payload.likedProperties || [];
      state.user.dislikedProperties = payload.dislikedProperties || [];
    },

    updateLoading(state, payload) {
      state.loading = payload;
    }
  },

  actions: {
    getUser({ state, commit }, token) {
      setAuthInterceptor(token);

      return backend.user.getDetails()
        .then(({ data }) => commit('updateUser', { authenticated: true, ...data }))
        .catch(({ data }) => console.log(data))
    },

    setUser({ state, commit }, payload) {
      commit('updateUser', { authenticated: true, ...payload });
      window.localStorage.setItem('token', payload.token);
      setAuthInterceptor(payload.token);
    },

    searchProperties({ state, commit }) {
      commit('updateLoading', true);

      const SelectedPropertyTypes = state.filters.propertyMacroTypes
        .map(id => {
          const PropertyTypeIds = state.propertyTypes
            .filter(type => type.PropertyMacroTypeId === id)
            .map(match => match.Id)

          return PropertyTypeIds;
        })
        .reduce((a, b) => [...a, ...b], [])

      const ListingsToExclude = [
        ...state.user.dislikedProperties.map(el => el.property_id),
        ...state.user.likedProperties.map(el => el.property_id)
      ]

      const search = {
        Criteria: {
          Residential: true,
          ForRent: !state.filters.buying,
          ForSale: state.filters.buying,
          TownIds: state.filters.towns,
          PriceFrom: state.filters.priceRange.From,
          PriceTo: state.filters.priceRange.To,
          SelectedPropertyTypes,
          ListingsToExclude,
          ListingScore: 50,
          Favourites: []
        },
        Skip: 0,
        Take: 30
      }

      return remax.searchProperties(search)
        .then(({ data }) => {
          const { NumberOfReturnedListings, TotalSearchResults } = data;
          commit('updateProperties', data.Listings);
          commit('updateSearchMeta', { NumberOfReturnedListings, TotalSearchResults });
        })
        .catch(({ data }) => console.log(data))
        .finally(() => commit('updateLoading', false))
    },

    getLikedPropertiesData({ state, commit }, property) {
      commit('updateLoading', true);

      const search = {
        Criteria: {
          Favourites: state.user.likedProperties.map(el => el.property_id)
        }
      }

      return remax.searchProperties(search)
        .then(({ data }) => commit('updateLikedPropertiesData', data.Listings))
        .catch(({ response }) => {
          console.log(response);
        })
        .finally(() => commit('updateLoading', false))
    },

    addLikedProperty({ state, commit }, mls) {
      const liked = [...state.user.likedProperties, { property_id: mls }];
      const properties = state.properties.filter(el => el.MLS !== mls);
      commit('updateLikedProperties', liked);
      commit('updateProperties', properties);

      if (state.user.authenticated) backend.user.likeProperty(mls);
    },

    removeLikedProperty({ state, commit }, mls) {
      const liked = state.user.likedProperties.filter(el => el.property_id !== mls);
      const properties = state.likedPropertiesData.filter(el => el.MLS !== mls);
      commit('updateLikedProperties', liked);
      commit('updateLikedPropertiesData', properties);

      if (state.user.authenticated) backend.user.unlikeProperty(mls);
    },

    addDislikedProperty({ state, commit }, mls) {
      const disliked = [...state.user.dislikedProperties, { property_id: mls }];
      const properties = state.properties.filter(el => el.MLS !== mls);
      commit('updateDislikedProperties', disliked);
      commit('updateProperties', properties);

      if (state.user.authenticated) backend.user.dislikeProperty(mls);
    }
  },

  getters: {
    getProvinceTownsById: (state) => (id) => {
      return state.towns.filter(town => town.ProvinceId === id);
    },

    totalPropertiesCount(state) {
      return state.properties.length;
    },

    likedPropertiesCount(state) {
      return state.user.likedProperties.length;
    }
  }
})
